/**
 * CarnetPage — Accès direct au Carnet PRO depuis le tableau de bord.
 *
 * URL : /carnet/:parcel_id
 * Différence avec /map?parcel=... : pas de chargement de la carte complète.
 * Optimisé pour les agriculteurs qui suivent déjà leurs parcelles verrouillées
 * et n'ont pas besoin de re-charger toute la carte satellite.
 */
import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, ClipboardList, Cloud, Loader2, Map, Move, Radar } from "lucide-react";
import { authFetch } from "@/lib/api";
import { toast } from "sonner";
import CarnetParcelle from "@/components/CarnetParcelle";
import WeatherModal from "@/components/WeatherModal";
import SanitaryAlertsBell from "@/components/SanitaryAlertsBell";
import RecalibrateParcelModal from "@/components/RecalibrateParcelModal";
import VigieSanitaireTab from "@/components/carnet/VigieSanitaireTab";
import CadastreRefsLine from "@/components/carnet/CadastreRefsLine";
import RequestAccessButton from "@/components/RequestAccessButton";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function CarnetPage() {
  const { parcelId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") || undefined;
  const phytoAutoOpen = searchParams.get("new") === "1";
  const phytoFilter = searchParams.get("drafts") === "1" ? "draft" : undefined;
  // Ouvert depuis la carte : le retour y ramène, centrée sur la parcelle
  const backTo = searchParams.get("from") === "map" ? `/map?parcel=${encodeURIComponent(parcelId)}` : "/dashboard";
  const [parcel, setParcel] = useState(null);
  const [refData, setRefData] = useState(null);
  const [access, setAccess] = useState(null); // statut de verrou : can_access_carnet, locked_by_email…
  const [loading, setLoading] = useState(true);
  const [showWeather, setShowWeather] = useState(false);
  const [showRecalibrate, setShowRecalibrate] = useState(false);
  const [showVigie, setShowVigie] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const region = parcelId.startsWith("YT-") ? "mayotte" : "reunion";
        const [pRes, rRes, aRes] = await Promise.all([
          authFetch(`${API}/parcels/${parcelId}`),
          authFetch(`${API}/carnet/reference-data?region=${region}`),
          authFetch(`${API}/parcels/${parcelId}/lock/status`),
        ]);
        if (!pRes.ok) {
          toast.error("Parcelle introuvable");
          navigate("/dashboard", { replace: true });
          return;
        }
        const p = await pRes.json();
        const r = rRes.ok ? await rRes.json() : null;
        const a = aRes.ok ? await aRes.json() : { can_access_carnet: false };
        if (!cancelled) {
          setParcel(p);
          setRefData(r);
          setAccess(a);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [parcelId, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#FAFAF8" }}>
        <div className="flex flex-col items-center gap-2">
          <Loader2 className="w-8 h-8 animate-spin" style={{ color: "#2D6A4F" }} />
          <p className="text-sm text-gray-500">Chargement de la parcelle…</p>
        </div>
      </div>
    );
  }

  if (!parcel) return null;

  // Parcelle verrouillée par un autre compte (ou carnet réservé) : pas d'accès, demande au propriétaire
  if (!access?.can_access_carnet) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: "#FAFAF8" }} data-testid="carnet-no-access">
        <div className="w-full max-w-sm bg-white rounded-xl border p-4 space-y-3">
          <button onClick={() => navigate(backTo)} className="text-xs flex items-center gap-1" style={{ color: "#2D6A4F" }}>
            <ArrowLeft className="w-3.5 h-3.5" /> Retour
          </button>
          <p className="font-semibold text-sm">{parcel.name || parcel.parcel_id}</p>
          {access?.locked ? (
            <>
              <p className="text-xs text-gray-600">Cette parcelle est verrouillée par <span className="font-mono">{access.locked_by_email}</span>. Son carnet n'est visible qu'avec l'accord du propriétaire.</p>
              <RequestAccessButton parcelId={parcel.parcel_id} />
            </>
          ) : (
            <p className="text-xs text-gray-600">Le carnet de cette parcelle est réservé aux comptes Pro.</p>
          )}
        </div>
      </div>
    );
  }

  // Centroïde robuste pour Polygon ou MultiPolygon
  const computeCentroid = (geom) => {
    if (!geom?.coordinates?.length) return null;
    const t = String(geom.type || "").toLowerCase();
    let ring = null;
    if (t === "polygon") ring = geom.coordinates[0];
    else if (t === "multipolygon") ring = geom.coordinates[0]?.[0];
    if (!ring || !ring.length) return null;
    let sLat = 0, sLon = 0, n = 0;
    for (const pt of ring) {
      if (Array.isArray(pt) && pt.length >= 2) { sLon += pt[0]; sLat += pt[1]; n++; }
    }
    if (n === 0) return null;
    return { lat: sLat / n, lon: sLon / n };
  };
  const centroid = computeCentroid(parcel.geometry);
  const isMayotte = (parcel.parcel_id || "").startsWith("YT-");
  const lat = parcel.centroid_lat ?? centroid?.lat ?? (isMayotte ? -12.827 : -21.1151);
  const lon = parcel.centroid_lon ?? centroid?.lon ?? (isMayotte ? 45.166 : 55.5364);

  return (
    <div className="h-screen h-[100dvh] flex flex-col overflow-hidden" style={{ background: "#FAFAF8" }} data-testid="carnet-page">
      {/* Hauteur fixée à l'écran : les onglets défilent dans leur propre zone (sinon Safari mobile bloque le défilement). */}
      {/* Compact header */}
      <div className="shrink-0 z-40 bg-white/95 backdrop-blur border-b flex items-center justify-between px-3 sm:px-4 py-2">
        <button onClick={() => navigate(backTo)} className="p-1.5 rounded-lg hover:bg-black/5" data-testid="back-to-dashboard-btn">
          <ArrowLeft className="w-4 h-4" style={{ color: "#2D6A4F" }} />
        </button>
        <div className="flex-1 mx-2 truncate">
          <div className="text-[10px] uppercase tracking-widest text-gray-500">Carnet PRO</div>
          <div className="text-sm font-bold truncate" style={{ color: "#1A1A1A" }}>{parcel.name || parcel.parcel_id}</div>
          <CadastreRefsLine parcelId={parcel.parcel_id} />
        </div>
        <button onClick={() => navigate(`/parcelles/${encodeURIComponent(parcel.parcel_id)}/fiche`)}
          className="h-11 px-3 mr-1 rounded-full flex items-center gap-1.5 text-xs font-semibold hover:bg-black/5"
          style={{ color: "#2D6A4F" }} data-testid="carnet-fiche-parcelle-btn">
          <ClipboardList className="w-4 h-4" /> <span className="hidden sm:inline">Fiche parcelle</span><span className="sm:hidden">Fiche</span>
        </button>
        <SanitaryAlertsBell />
      </div>

      {/* Carnet content */}
      <div className="flex-1 min-h-0 flex flex-col overflow-hidden" style={{ paddingBottom: "72px" }}>
        <CarnetParcelle
          parcel={parcel}
          refData={refData}
          onClose={() => navigate(backTo)}
          hideHeader
          initialTab={initialTab}
          phytoAutoOpen={phytoAutoOpen}
          phytoFilter={phytoFilter}
        />
      </div>

      {/* Sticky footer with quick actions (weather, map view, recalibrate) */}
      <div className="fixed bottom-0 left-0 right-0 z-40 bg-white border-t p-2 pb-safe">
        <div className="max-w-3xl mx-auto flex flex-wrap items-center justify-center gap-1.5 sm:gap-2">
          <button
            onClick={() => setShowWeather(true)}
            data-testid="open-weather-btn"
            className="flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-full text-white text-[11px] sm:text-sm font-medium shadow-md hover:shadow-lg transition-all"
            style={{ background: "linear-gradient(135deg, #0891B2, #0E7490)" }}>
            <Cloud className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            <span className="hidden sm:inline">Météo sur ma parcelle</span>
            <span className="sm:hidden">Météo</span>
          </button>
          <button
            onClick={() => navigate(`/map?parcel=${parcel.parcel_id}`)}
            data-testid="open-map-btn"
            className="flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-full text-white text-[11px] sm:text-sm font-medium shadow-md hover:shadow-lg transition-all"
            style={{ background: "linear-gradient(135deg, #2D6A4F, #1B4332)" }}>
            <Map className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            <span className="hidden sm:inline">Voir sur la carte</span>
            <span className="sm:hidden">Carte</span>
          </button>
          <button
            onClick={() => setShowVigie(true)}
            data-testid="open-vigie-btn"
            className="flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-full text-white text-[11px] sm:text-sm font-medium shadow-md hover:shadow-lg transition-all"
            style={{ background: "linear-gradient(135deg, #059669, #047857)" }}
            title="Alerte bioagresseurs à l'instant T (météo + sol + satellite)">
            <Radar className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            <span className="hidden sm:inline">Vigie Sanitaire</span>
            <span className="sm:hidden">Vigie</span>
          </button>
          <button
            onClick={() => setShowRecalibrate(true)}
            data-testid="open-recalibrate-btn"
            className="flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-full text-white text-[11px] sm:text-sm font-medium shadow-md hover:shadow-lg transition-all"
            style={{ background: "linear-gradient(135deg, #D97706, #B45309)" }}
            title="Corriger manuellement le contour en cas d'erreur de détection">
            <Move className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            <span className="hidden sm:inline">Recalibrer la parcelle</span>
            <span className="sm:hidden">Recalibrer</span>
          </button>
        </div>
      </div>

      {showWeather && (
        <WeatherModal lat={lat} lon={lon} parcelName={parcel.name} onClose={() => setShowWeather(false)} />
      )}

      {showRecalibrate && (
        <RecalibrateParcelModal
          parcel={parcel}
          onClose={() => setShowRecalibrate(false)}
          onSaved={(updated) => {
            setShowRecalibrate(false);
            if (updated) setParcel(updated);
            else {
              // Reset case: refetch parcel
              authFetch(`${API}/parcels/${parcelId}`).then(r => r.ok ? r.json() : null).then(p => p && setParcel(p));
            }
          }}
        />
      )}

      {showVigie && (
        <VigieSanitaireTab
          parcelId={parcel.parcel_id}
          cultureType={parcel.culture_type}
          onClose={() => setShowVigie(false)}
        />
      )}
    </div>
  );
}
